import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Props = {
  label: ReactNode
  description?: ReactNode
  /** Associates the label with a form control for screen readers. */
  htmlFor?: string
  /**
   * `field` gives text inputs and selects a fixed column; `auto` keeps the
   * control at its natural width (switches, buttons, previews).
   */
  control?: 'field' | 'auto'
  /** Inline error shown under the control. */
  error?: ReactNode
  className?: string
  children?: ReactNode
}

/**
 * The single settings row pattern (design 15): label and help text on the
 * left, the control on the right. At phone width the label stacks above the
 * control.
 */
export function SettingsRow({
  label,
  description,
  htmlFor,
  control = 'auto',
  error,
  className,
  children,
}: Props) {
  const Label = htmlFor ? 'label' : 'div'
  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-[18px] py-3.5 sm:min-h-[66px] sm:flex-row sm:items-center sm:justify-between sm:gap-6',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        <Label
          {...(htmlFor ? { htmlFor } : {})}
          className="block text-sm font-medium text-foreground"
        >
          {label}
        </Label>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      {children !== undefined && (
        <div
          className={cn(
            'flex min-w-0 flex-col gap-1',
            control === 'field'
              ? 'w-full sm:w-[300px] sm:shrink-0'
              : 'sm:shrink-0 sm:items-end',
          )}
        >
          {children}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}
    </div>
  )
}
